/**
 * ReferralChannelsPage — referral チャネル（SNS 広告 / QR / 紙媒体等）プリセット管理。
 *
 * 用途:
 *   - キャンペーンボードの referral カードや UrlGeneratorPage で選択肢として参照される辞書
 *   - チャネルごとに固有の refTag を発行し、命名揺れを防止
 */
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useCreateReferralChannel,
  useDeleteReferralChannel,
  useReferralChannels,
  useUpdateReferralChannel,
  type ReferralChannelRecord,
} from '@/hooks/useReferralChannels';

import { REFERRAL_TAG_RE } from '@goenhub/shared';

import { Link as LinkIcon } from 'lucide-react';

interface FormState {
  name: string;
  refTag: string;
  category: string;
  description: string;
  color: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  refTag: '',
  category: '',
  description: '',
  color: '',
};

/**
 * referral チャネル管理ビュー（リフェラル統合ページのタブ用）。
 * Screen ラッパは持たず、アクションボタン（新規チャネル）は内部に表示。
 */
export function ReferralChannelsView() {
  const { user } = useAuth();
  const { data, isLoading } = useReferralChannels();
  const createMutation = useCreateReferralChannel();
  const updateMutation = useUpdateReferralChannel();
  const deleteMutation = useDeleteReferralChannel();

  const [editing, setEditing] = useState<ReferralChannelRecord | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const open = creating || editing !== null;
  const refTagValid = !form.refTag || REFERRAL_TAG_RE.test(form.refTag);
  const canSubmit = !!form.name && !!form.refTag && refTagValid;

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name ?? '',
        refTag: editing.refTag ?? '',
        category: editing.category ?? '',
        description: editing.description ?? '',
        color: editing.color ?? '',
      });
    } else if (creating) {
      setForm(EMPTY_FORM);
    }
  }, [editing, creating]);

  const closeDialog = () => {
    setCreating(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        await updateMutation.mutateAsync({
          id: editing.id,
          data: {
            name: form.name,
            refTag: form.refTag,
            category: form.category || undefined,
            description: form.description || undefined,
            color: form.color || undefined,
          },
        });
        toast.success('更新しました');
      } else {
        await createMutation.mutateAsync({
          name: form.name,
          refTag: form.refTag,
          category: form.category || undefined,
          description: form.description || undefined,
          color: form.color || undefined,
          createdBy: user?.uid ?? '',
        });
        toast.success('チャネルを作成しました');
      }
      closeDialog();
    } catch (err) {
      toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDelete = async (ch: ReferralChannelRecord) => {
    if (
      !window.confirm(
        `「${ch.name}」を削除します。既に発行済みの URL に影響はありませんが、新規生成時の選択肢から外れます。`,
      )
    )
      return;
    try {
      await deleteMutation.mutateAsync(ch.id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[var(--text-muted)]">
          SNS 広告 / QR / 紙チラシ等、外部チャネル毎の識別タグを管理します。キャンペーンボードの referral カードや「URL 発行」タブで選択肢として参照されます。
        </p>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => setCreating(true)}
        >
          <Plus className="size-3.5 mr-1" />
          新規チャネル
        </Button>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <Spinner />
        </div>
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={LinkIcon}
          title="チャネルがまだ登録されていません"
          description="広告媒体・QR・紙媒体・インフルエンサー施策など、流入チャネル単位で登録してください。"
        />
      ) : (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {data.map((ch) => (
            <div
              key={ch.id}
              className="flex items-center gap-3 p-3 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)]"
            >
              <span
                className="size-2.5 rounded-full flex-shrink-0"
                style={{ background: ch.color || 'var(--accent)' }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-medium truncate">{ch.name}</span>
                  <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-muted)]">
                    ref: {ch.refTag}
                  </span>
                  {ch.category && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--accent-subtle)] text-[var(--accent)]">
                      {ch.category}
                    </span>
                  )}
                </div>
                {ch.description && (
                  <div className="text-xs text-[var(--text-muted)] truncate mt-0.5">
                    {ch.description}
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setEditing(ch)}
                title="編集"
              >
                <Pencil className="size-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => void handleDelete(ch)}
                title="削除"
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'チャネルを編集' : 'チャネルを新規作成'}
            </DialogTitle>
            <DialogDescription>
              refTag は audience_events.metadata.refTag に保存される識別子。半角英数字・_-. の 32 文字以内。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="rch-name">表示名</Label>
              <Input
                id="rch-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="例: Facebook 広告 A 案"
              />
            </div>
            <div>
              <Label htmlFor="rch-tag">refTag（URL に入る識別子）</Label>
              <Input
                id="rch-tag"
                type="text"
                value={form.refTag}
                onChange={(e) =>
                  setForm((f) => ({ ...f, refTag: e.target.value.trim() }))
                }
                placeholder="例: fb_creativeA"
                className="font-mono"
              />
              {form.refTag && !refTagValid && (
                <p className="text-[11px] text-[var(--accent-amber)] mt-1">
                  半角英数字・アンダースコア・ハイフン・ドットの 32 文字以内
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="rch-cat">カテゴリ（任意）</Label>
                <Input
                  id="rch-cat"
                  type="text"
                  value={form.category}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, category: e.target.value }))
                  }
                  placeholder="例: SNS 広告 / QR / 紙媒体"
                />
              </div>
              <div>
                <Label htmlFor="rch-color">カラー（任意）</Label>
                <Input
                  id="rch-color"
                  type="color"
                  value={form.color || '#4a7cff'}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, color: e.target.value }))
                  }
                />
              </div>
            </div>
            <div>
              <Label htmlFor="rch-desc">メモ（任意）</Label>
              <Textarea
                id="rch-desc"
                rows={2}
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="運用上のメモ・配信期間など"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={closeDialog}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleSubmit()}
              disabled={
                !canSubmit ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {(createMutation.isPending || updateMutation.isPending)
                ? '保存中...'
                : editing
                  ? '更新'
                  : '作成'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
